import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import HotelLatestProposalSummary from './HotelLatestProposalSummary';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  isHotelProposalBundle,
  type HotelProposalBundle,
  type ProposalBundle,
  type TransportProposalBundle
} from '../../types/proposalBundles';
import PairRoundTripCard from './PairRoundTripCard';
import TransportLatestProposalSummary from './TransportLatestProposalSummary';

const LATEST_PROPOSAL_ROW_KIND = {
  ROUND_TRIP_PAIR: 'roundTripPair',
  HOTEL: 'hotel',
  TRANSPORT: 'transport'
} as const;

type LatestProposalRow =
  | {
      kind: typeof LATEST_PROPOSAL_ROW_KIND.ROUND_TRIP_PAIR;
      outward: TransportProposalBundle;
      homeward: TransportProposalBundle;
    }
  | { kind: typeof LATEST_PROPOSAL_ROW_KIND.HOTEL; bundle: HotelProposalBundle }
  | { kind: typeof LATEST_PROPOSAL_ROW_KIND.TRANSPORT; bundle: TransportProposalBundle };

interface Props {
  bundles: ProposalBundle[];
  includeTax?: boolean;
}

const LatestProposals = ({ bundles, includeTax = false }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const rows = useMemo(() => buildLatestProposalRows(bundles), [bundles]);

  if (bundles.length === 0) {
    return <p style={styles.placeholder}>提案内容がまだありません</p>;
  }

  return (
    <div style={styles.container}>
      {rows.map(row => {
        const key = latestProposalRowKey(row);
        switch (row.kind) {
          case LATEST_PROPOSAL_ROW_KIND.ROUND_TRIP_PAIR:
            return (
              <PairRoundTripCard key={key} outward={row.outward} homeward={row.homeward} includeTax={includeTax} />
            );
          case LATEST_PROPOSAL_ROW_KIND.HOTEL:
            return <HotelLatestProposalSummary key={key} bundle={row.bundle} includeTax={includeTax} />;
          case LATEST_PROPOSAL_ROW_KIND.TRANSPORT:
            return <TransportLatestProposalSummary key={key} bundle={row.bundle} includeTax={includeTax} />;
        }
      })}
    </div>
  );
};

const findRoundTripLegBundles = (bundles: ProposalBundle[]) => {
  const outward = bundles.find(
    (bundle): bundle is TransportProposalBundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD
  );
  const homeward = bundles.find(
    (bundle): bundle is TransportProposalBundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  );
  return { outward, homeward };
};

const filterNonRoundTripLegBundles = (bundles: ProposalBundle[]): ProposalBundle[] =>
  bundles.filter(
    bundle =>
      bundle.category !== PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.category !== PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  );

const toLatestProposalRow = (bundle: ProposalBundle): LatestProposalRow => {
  if (isHotelProposalBundle(bundle)) {
    return { kind: LATEST_PROPOSAL_ROW_KIND.HOTEL, bundle };
  }

  return { kind: LATEST_PROPOSAL_ROW_KIND.TRANSPORT, bundle };
};

const buildSingleLegRows = (
  outward: ProposalBundle | undefined,
  homeward: ProposalBundle | undefined,
  rest: ProposalBundle[]
): LatestProposalRow[] => [
  ...(outward != null ? [toLatestProposalRow(outward)] : []),
  ...(homeward != null ? [toLatestProposalRow(homeward)] : []),
  ...rest.map(toLatestProposalRow)
];

const buildLatestProposalRows = (bundles: ProposalBundle[]): LatestProposalRow[] => {
  const { outward, homeward } = findRoundTripLegBundles(bundles);
  const rest = filterNonRoundTripLegBundles(bundles);

  if (outward != null && homeward != null) {
    return [
      { kind: LATEST_PROPOSAL_ROW_KIND.ROUND_TRIP_PAIR, outward, homeward },
      ...rest.map(toLatestProposalRow)
    ];
  }

  return buildSingleLegRows(outward, homeward, rest);
};

const latestProposalRowKey = (row: LatestProposalRow): string => {
  switch (row.kind) {
    case LATEST_PROPOSAL_ROW_KIND.ROUND_TRIP_PAIR:
      return `${LATEST_PROPOSAL_ROW_KIND.ROUND_TRIP_PAIR}-${row.outward.id}-${row.homeward.id}`;
    case LATEST_PROPOSAL_ROW_KIND.HOTEL:
      return `${LATEST_PROPOSAL_ROW_KIND.HOTEL}-${row.bundle.id}`;
    case LATEST_PROPOSAL_ROW_KIND.TRANSPORT:
      return `${LATEST_PROPOSAL_ROW_KIND.TRANSPORT}-${row.bundle.id}`;
  }
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginTop: '12px'
  },
  placeholder: {
    textAlign: 'center' as const,
    color: theme.grayTextColor,
    margin: '16px 0 0',
    fontSize: '13px'
  }
});

export default LatestProposals;
